import { useCallback, useEffect } from "react";

import { datadogRum } from "@datadog/browser-rum";
import { getCookie } from "cookies-next";

import { useClientEnvironment } from "~/app/environment/client";
import { useProfileData } from "~/queries/profile/useProfile";
import { ACCESS_TOKEN_COOKIE } from "~/utils/constants";

/**
 * Lazy-loads the Intercom messenger SDK on the client and initializes it with
 * the current user's identity (or anonymously when logged out).
 *
 * The init runs once the user state is resolved:
 * - Logged-out visitors (no access token cookie) → initialize anonymously.
 * - Logged-in visitors → wait for the profile to load, then initialize with
 *   identity verification (`user_hash`).
 *
 * Used on routes where Intercom-triggered modals/messengers need to be
 * available (listing pages, vendor pages, etc.).
 */
export function useIntercom() {
  const { INTERCOM_APP_ID } = useClientEnvironment();
  const profile = useProfileData();

  const initializeIntercom = useCallback(async () => {
    try {
      const Intercom = (await import("@intercom/messenger-js-sdk")).default;
      Intercom({
        app_id: INTERCOM_APP_ID,
        user_id: profile?.SSOid || "",
        name: profile?.firstName,
        user_hash: profile?.intercomHmac,
        "Account Type": profile?.isHost ? "Host" : "Guest",
      });
    } catch (e) {
      datadogRum.addError(e);
    }
  }, [
    INTERCOM_APP_ID,
    profile?.SSOid,
    profile?.firstName,
    profile?.intercomHmac,
    profile?.isHost,
  ]);

  const hasProfile = Boolean(profile);

  useEffect(() => {
    const accessToken = getCookie(ACCESS_TOKEN_COOKIE) as string;
    if (!accessToken || hasProfile) {
      initializeIntercom();
    }
  }, [hasProfile, initializeIntercom]);
}
